import operationTypes from 'constants/operationTypes';
import normalizeCif from 'utils/normalizeCif';

import { parseExcelDate } from './parseExcelDate';
import { parseExcelInteger } from './parseExcelInteger';
import { parseExcelPrice } from './parseExcelPrice';
import {
  SharesTransferExcelRawRow,
  SharesTransferExcelRow,
  SharesTransferExcelValidationError,
} from '../types/SharesTransferExcel';

const ALLOWED_OPERATION_TYPES = new Set<string>([
  operationTypes.SELL_PARTICIPATION,
  operationTypes.HERITAGE,
  operationTypes.DONATION,
  operationTypes.ENDORSEMENT,
  operationTypes.TRANSMISSION,
]);

type MapRawRowsResult = {
  rows: SharesTransferExcelRow[];
  errors: SharesTransferExcelValidationError[];
};

export const mapRawRowsToSharesTransferExcelRows = (
  rawRows: SharesTransferExcelRawRow[]
): MapRawRowsResult => {
  const rows: SharesTransferExcelRow[] = [];
  const errors: SharesTransferExcelValidationError[] = [];

  if (!rawRows.length) {
    errors.push({ code: 'NO_ROWS' });
    return { rows, errors };
  }

  rawRows.forEach((rawRow, index) => {
    const rowIndex = index + 2;
    const operationId = String(rawRow.OPERATION_ID || '').trim();

    if (!operationId) {
      errors.push({ code: 'MISSING_OPERATION_ID', rowIndex });
      return;
    }

    const cif = normalizeCif(String(rawRow.CIF || ''));
    const name = String(rawRow.NAME || '').trim();
    const totalShares = parseExcelInteger(rawRow.TOTAL_SHARES);
    const shareFrom = parseExcelInteger(rawRow.SHARE_FROM);
    const shareTo = parseExcelInteger(rawRow.SHARE_TO);
    const date = parseExcelDate(rawRow.DATE);
    const operationType = String(rawRow.OPERATION_TYPE || '').trim();

    if (!cif) {
      errors.push({ code: 'MISSING_CIF', operationId, rowIndex });
    }

    if (!name) {
      errors.push({ code: 'MISSING_NAME', operationId, rowIndex, cif });
    }

    if (!date) {
      errors.push({ code: 'INVALID_DATE', operationId, rowIndex, cif });
    }

    if (!operationType || !ALLOWED_OPERATION_TYPES.has(operationType)) {
      errors.push({
        code: 'INVALID_OPERATION_TYPE',
        operationId,
        rowIndex,
        cif,
        detail: operationType,
      });
    }

    if (
      totalShares === null ||
      shareFrom === null ||
      shareTo === null ||
      shareFrom <= 0 ||
      shareTo < shareFrom
    ) {
      errors.push({ code: 'INVALID_RANGE', operationId, rowIndex, cif });
    }

    if (
      totalShares !== null &&
      shareFrom !== null &&
      shareTo !== null &&
      Math.abs(totalShares) !== shareTo - shareFrom + 1
    ) {
      errors.push({
        code: 'RANGE_SHARES_MISMATCH',
        operationId,
        rowIndex,
        cif,
      });
    }

    if (totalShares === 0) {
      errors.push({ code: 'INVALID_RANGE', operationId, rowIndex, cif });
    }

    rows.push({
      rowIndex,
      operationId,
      name,
      cif,
      email: String(rawRow.EMAIL || '').trim(),
      operationType,
      totalShares: totalShares ?? 0,
      shareFrom: shareFrom ?? 0,
      shareTo: shareTo ?? 0,
      date: date ?? new Date(0),
      sharePrice: parseExcelPrice(rawRow.SHARE_PRICE),
      alias: String(rawRow.ALIAS || '').trim(),
      comments: String(rawRow.COMMENTS || '').trim(),
      isSeller: (totalShares ?? 0) < 0,
    });
  });

  return { rows, errors };
};

export { ALLOWED_OPERATION_TYPES };
